import React, { useState, useRef } from 'react';
import LoggingService from '../../Services/LoggingService';
import { VoidReturn } from '../../Utils/PageUtils';
import { SliderStateType } from './CustomSlider';

export type SliderHandle = {
    sliderPos: number;
    loading: boolean;
    onClickAdjustButton: (value: number) => VoidReturn;
    handleOnChangeCommittedSlider: (value: number | number[]) => VoidReturn;
    handleOnChangeSlider: (value: number | number[]) => VoidReturn;
    handleSliderStart: (event: React.TouchEvent<HTMLElement>) => VoidReturn;
    handleSliderMove: () => VoidReturn;
    handleSliderCancel: () => VoidReturn;
};

function useCustomSliderHandler(
    handleSliderChange: (value: number) => void,
    handleSliderChangeCommitted: (value: number) => void,
    sliderState: SliderStateType,
    onSliderStart?: (event: React.TouchEvent<HTMLElement>) => void,
    handleSliderButtonClick?: (value: number) => void
): SliderHandle {
    const [loading, setLoading] = useState(false);

    const [sliderPos, setSliderPos] = useState(sliderState.initialPosition);
    const lastCommittedValueRef = useRef<number | null>(null);
    const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSliderChangeWithLoading = async (value: number) => {
        setLoading(true);
        await handleSliderChangeCommitted(value);
        setLoading(false);
    };

    const onClickAdjustButton = async (value: number) => {
        setLoading(true);
        let currentSliderPos = sliderPos + value;
        if (
            currentSliderPos >= sliderState.posMin &&
            currentSliderPos <= sliderState.posMax
        ) {
            LoggingService.info({
                args: [`adjust slider ${currentSliderPos}`],
            });
        } else if (
            currentSliderPos < sliderState.posMin &&
            sliderPos !== sliderState.posMin
        ) {
            currentSliderPos = sliderState.posMin;
        } else if (
            currentSliderPos > sliderState.posMax &&
            sliderPos !== sliderState.posMax
        ) {
            currentSliderPos = sliderState.posMax;
        } else {
            return;
        }
        if (handleSliderButtonClick)
            await handleSliderButtonClick(currentSliderPos);
        await handleSliderChangeWithLoading(currentSliderPos);
        setSliderPos(currentSliderPos);
        setLoading(false);
    };

    const handleOnChangeCommittedSlider = async (value: number | number[]) => {
        const numericValue = Number(value);

        if (lastCommittedValueRef.current === numericValue) {
            return;
        }

        lastCommittedValueRef.current = numericValue;
        await handleSliderChangeWithLoading(numericValue);
    };

    const handleOnChangeSlider = (value: number | number[]) => {
        const numericValue = Number(value);

        lastCommittedValueRef.current = null;
        setSliderPos(numericValue);
        handleSliderChange(numericValue);
        if (commitTimeoutRef.current) {
            clearTimeout(commitTimeoutRef.current);
        }

        commitTimeoutRef.current = setTimeout(() => {
            void handleOnChangeCommittedSlider(numericValue);
        }, 300);
    };

    const handleSliderStart = (event: React.TouchEvent<HTMLElement>): void => {
        event.preventDefault();
        if (onSliderStart) {
            onSliderStart(event);
        }
    };
    const handleSliderMove = (): void => {
        // Intentionally left empty; updates are handled via onChange.
    };

    const handleSliderCancel = (): VoidReturn => {
        return handleOnChangeCommittedSlider(sliderPos);
    };

    return {
        sliderPos,
        loading,
        onClickAdjustButton,
        handleOnChangeCommittedSlider,
        handleOnChangeSlider,
        handleSliderStart,
        handleSliderMove,
        handleSliderCancel,
    };
}

export default useCustomSliderHandler;
